import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faSpinner, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { apiService } from '@/utils/api';
import { KustomAuthentication as KustomAuthType } from '@/types';

const KustomAuthentication: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAuth, setEditingAuth] = useState<KustomAuthType | null>(null);
  const [showPasswords, setShowPasswords] = useState<Record<number, boolean>>({});
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    live_mode: false,
  });

  const queryClient = useQueryClient();

  // Get first site ID (matching original behavior)
  const { data: sites = [] } = useQuery('sites', apiService.getSites);
  const siteId = sites.length > 0 ? sites[0].id : null;

  // Fetch Kustom auths
  const { data: kustomAuths = [], isLoading } = useQuery(
    ['kustomAuths', siteId],
    () => apiService.getKustomAuths(siteId!),
    {
      enabled: !!siteId,
    }
  );

  // Create/Update mutation
  const saveMutation = useMutation(
    (data: any) => {
      if (editingAuth) {
        return apiService.updateKustomAuth(siteId!, editingAuth.id!, data);
      } else {
        return apiService.createKustomAuth(siteId!, data);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['kustomAuths', siteId]);
        handleCloseModal();
      },
      onError: (error) => {
        console.error('Save failed:', error);
        alert('Failed to save Kustom auth');
      },
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    (id: number) => apiService.deleteKustomAuth(siteId!, id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['kustomAuths', siteId]);
      },
      onError: (error) => {
        console.error('Delete failed:', error);
        alert('Failed to delete Kustom auth');
      },
    }
  );

  const handleOpenModal = (auth?: KustomAuthType) => {
    if (auth) {
      setEditingAuth(auth);
      setFormData({
        username: auth.username || '',
        password: auth.password || '',
        live_mode: auth.live_mode || false,
      });
    } else {
      setEditingAuth(null);
      setFormData({
        username: '',
        password: '',
        live_mode: false,
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingAuth(null);
    setFormData({
      username: '',
      password: '',
      live_mode: false,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const submitData = {
      ...formData,
      site_id: siteId,
    };

    saveMutation.mutate(submitData);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to delete this Kustom auth?')) {
      deleteMutation.mutate(id);
    }
  };

  const togglePasswordVisibility = (id: number) => {
    setShowPasswords(prev => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="section-title">Kustom Auths</h1>
        <button
          onClick={() => handleOpenModal()}
          className="px-4 py-2 text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 flex items-center space-x-2"
          style={{ backgroundColor: 'hsl(15 100% 52%)' }}
        >
          <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
          <span>Add Kustom Auth</span>
        </button>
      </div>

      <div className="card">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Site ID</th>
                <th>Username</th>
                <th>Password</th>
                <th>Live Mode</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {kustomAuths.map((auth: KustomAuthType) => (
                <tr key={auth.id}>
                  <td className="font-mono text-sm">{auth.id}</td>
                  <td>{auth.site_id}</td>
                  <td className="font-mono text-sm">{auth.username}</td>
                  <td>
                    <div className="flex items-center space-x-2">
                      <span className="font-mono text-sm">
                        {showPasswords[auth.id] ? auth.password : '••••••••'}
                      </span>
                      <button
                        onClick={() => togglePasswordVisibility(auth.id)}
                        className="btn-icon btn-icon-secondary"
                        title={showPasswords[auth.id] ? 'Hide password' : 'Show password'}
                      >
                        <FontAwesomeIcon
                          icon={showPasswords[auth.id] ? faEyeSlash : faEye}
                          className="w-3 h-3"
                        />
                      </button>
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${auth.live_mode ? 'badge-success' : 'badge-secondary'}`}>
                      {auth.live_mode ? 'Live' : 'Test'}
                    </span>
                  </td>
                  <td className="text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleOpenModal(auth)}
                        className="btn-icon btn-icon-secondary"
                        title="Edit"
                      >
                        <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(auth.id)}
                        className="btn-icon btn-icon-danger"
                        title="Delete"
                        disabled={deleteMutation.isLoading}
                      >
                        {deleteMutation.isLoading ? (
                          <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                        ) : (
                          <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingAuth ? `Edit Kustom Auth #${editingAuth.id}` : 'New Kustom Auth'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="modal-close"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-body">
              <div className="form-section">
                <div className="form-row">
                  <div className="form-column">
                    <div>
                      <label className="form-label form-label-required">Username:</label>
                      <input
                        type="text"
                        value={formData.username}
                        onChange={(e) => handleInputChange('username', e.target.value)}
                        className="form-input"
                        required
                        placeholder="Enter Kustom username"
                      />
                    </div>

                    <div>
                      <label className="form-label form-label-required">Password:</label>
                      <input
                        type="text"
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        className="form-input"
                        required
                        placeholder="Enter Kustom password"
                      />
                    </div>

                    <div className="flex items-center pt-4">
                      <input
                        type="checkbox"
                        id="live_mode"
                        checked={formData.live_mode}
                        onChange={(e) => handleInputChange('live_mode', e.target.checked)}
                        className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                      />
                      <label htmlFor="live_mode" className="ml-2 text-sm font-medium text-gray-700">
                        Live Mode
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-6 space-x-3">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saveMutation.isLoading}
                    className="px-4 py-2 text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 flex items-center space-x-2"
          style={{ backgroundColor: 'hsl(15 100% 52%)' }}
                  >
                    {saveMutation.isLoading ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                        <span>Saving...</span>
                      </>
                    ) : (
                      <span>Save</span>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default KustomAuthentication;

